'use client';

import { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Achievement {
  id: string;
  title: string;
  event: string;
  year: string;
  rank: string;
  location: string;
  accentColor: string;
  summary: string;
  tags: string[];
  postUrl: string;
  certificateImage: string;
}

const ACHIEVEMENTS: Achievement[] = [
  {
    id: 'ACH-001',
    title: 'Winner of Best Use of GenAI',
    event: 'HACKINSPIRATION23 BY MLSC PUNE',
    year: '2024',
    rank: 'VIT, Pune',
    location: 'Pune, India',
    accentColor: 'var(--cyber-magenta)',
    summary:
      'Built AI-powered accessibility tool using computer vision to detect American Sign Language hand gestures with95% accuracy in real-time',
    tags: ['Hackathon', 'Team Leadership', 'Rapid Prototyping'],
    postUrl: 'https://www.linkedin.com/',
    certificateImage:
      '/hackinspiration.jpg',
  },
  {
    id: 'ACH-002',
    title: 'Winner - BuzzOnEarth Climate Change Hackathon',
    event: 'BuzzOnEarth with IIT Kanpur',
    year: '2023',
    rank: '1st',
    location: 'Remote',
    accentColor: 'var(--cyber-cyan)',
    summary:
      'Developed sustainable packaging solutions for consumer goods using banana fiber that reduce waste and minimize the environmental impact of packaging throughout its lifecycle.',
    tags: ['Sustainability', 'Environment', 'Banana Fiber','Recycling'],
    postUrl: 'https://www.linkedin.com/posts/niranjan45_sustainablepackaging-sustainability-environment-activity-7060687688336650240-K9xd?utm_source=social_share_send&utm_medium=member_desktop_web&rcm=ACoAAD-azAsB24SreRBOGkyo8COIX2-VbNuMMoA',
    certificateImage:
      '/BuzzOnEarth.jpg',
  },
  {
    id: 'ACH-003',
    title: 'HIC Innovation Day',
    event: 'Philips India Ltd',
    year: '2025',
    rank: '1st Place',
    location: 'Pune, India',
    accentColor: 'var(--cyber-green)',
    summary:
      'Engineered end-to-end SWO Data Analytics system processing 10,000+ service orders with AI classification and Power BI dashboards, reducing analysis time by 70%.',
    tags: ['Python', 'AI', 'PowerBI','Data Analytics'],
    postUrl: 'https://www.linkedin.com/',
    certificateImage:
      'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80',
  },
  {
    id: 'ACH-004',
    title: 'Finalist',
    event: 'AI Innovation Hackathon',
    year: '2026',
    rank: 'Finalist',
    location: 'CyberMeru, Pune, India',
    accentColor: 'var(--cyber-yellow)',
    summary:
      'Built a Small Langauge Model (SLM) from stratch trained on 15 Million parameters to generate short stories.',
    tags: ['AI', 'Transformers', 'Tokenization','LLM Architecture'],
    postUrl: 'https://www.linkedin.com/',
    certificateImage:
      'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80',
  },
];

export default function AchievementsSection() {
  const [previewImage, setPreviewImage] = useState<{ src: string; title: string } | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPreviewImage(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <section
      id="achievements"
      className="relative py-24 px-6 md:px-12 overflow-hidden"
      style={{ background: 'var(--cyber-dark)' }}
      aria-label="Achievements section"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(120deg, rgba(255,0,110,0.035) 0%, transparent 50%)' }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 45% at 50% 20%, rgba(0,212,255,0.06) 0%, transparent 65%)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="mb-16">
          <p className="font-mono text-xs mb-3" style={{ color: 'var(--cyber-magenta)', letterSpacing: '0.3em' }}>
             MODULE: 05 
          </p>
          <h2 className="font-mono font-black text-4xl md:text-6xl" style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            WIN<span className="text-glow-magenta">_PROTOCOL</span>
          </h2>
          <p className="font-mono text-sm mt-4" style={{ color: 'rgba(200,216,232,0.5)' }}>
            VERIFIED_ACHIEVEMENTS: {ACHIEVEMENTS.length} ENTRIES
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          {ACHIEVEMENTS.map((item, i) => {
            const isFeatured = i % 3 === 0;
            const isTrophy = i === 0;
            return (
            <article
              key={item.id}
              className={`relative overflow-hidden group transition-all duration-300 achievement-enter ${
                isFeatured ? 'md:col-span-7' : 'md:col-span-5'
              }`}
              style={{
                border: `1px solid ${item.accentColor}28`,
                background: isTrophy
                  ? 'linear-gradient(165deg, rgba(22,16,8,0.95) 0%, rgba(10,9,7,0.98) 100%)'
                  : 'linear-gradient(160deg, rgba(10,15,20,0.95) 0%, rgba(6,10,14,0.98) 100%)',
                clipPath: isTrophy
                  ? 'polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)'
                  : isFeatured
                  ? 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 0 100%)'
                  : 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)',
                animationDelay: `${i * 120}ms`,
              }}
            >
              <div
                className="absolute left-0 top-0 h-[2px] w-24 scan-line"
                style={{ background: `linear-gradient(90deg, transparent, ${item.accentColor}, transparent)` }}
              />
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{ background: `linear-gradient(90deg, transparent, ${item.accentColor}, transparent)` }}
              />
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{ background: `linear-gradient(135deg, ${item.accentColor}14 0%, transparent 45%)` }}
              />
              <div
                className="absolute top-3 right-3 w-3 h-3 border-r border-t"
                style={{ borderColor: `${item.accentColor}80` }}
              />
              <div
                className="absolute bottom-3 left-3 w-3 h-3 border-l border-b"
                style={{ borderColor: `${item.accentColor}80` }}
              />
              <div
                className="absolute -right-10 -top-10 w-28 h-28 rounded-full opacity-20 group-hover:opacity-35 transition-opacity duration-300"
                style={{ background: item.accentColor, filter: 'blur(24px)' }}
              />
              <div className={`relative ${isFeatured ? 'p-7 md:p-8' : 'p-6 md:p-7'}`}>
                {isTrophy ? (
                  <div className="grid grid-cols-[74px_1fr] gap-4 items-start">
                    <div
                      className="relative h-[74px] w-[74px] flex items-center justify-center"
                      style={{
                        border: `1px solid ${item.accentColor}45`,
                        background: `${item.accentColor}18`,
                        clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
                        boxShadow: `0 0 18px ${item.accentColor}25`,
                      }}
                    >
                      <Icon name="TrophyIcon" size={28} style={{ color: item.accentColor }} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span
                          className="font-mono text-[9px] px-2 py-0.5"
                          style={{
                            background: `${item.accentColor}12`,
                            border: `1px solid ${item.accentColor}35`,
                            color: item.accentColor,
                            letterSpacing: '0.14em',
                          }}
                        >
                          {item.id}
                        </span>
                        <span className="font-mono text-[10px]" style={{ color: 'rgba(200,216,232,0.5)', letterSpacing: '0.12em' }}>
                          {item.year}
                        </span>
                      </div>
                      <h3 className="font-mono font-black text-xl mb-1" style={{ color: '#FFFFFF' }}>
                        {item.title}
                      </h3>
                      <p className="font-mono text-sm mb-2" style={{ color: item.accentColor }}>
                        {item.event}
                      </p>
                      <div
                        className="inline-flex font-mono text-[10px] px-2 py-1"
                        style={{
                          color: item.accentColor,
                          border: `1px solid ${item.accentColor}40`,
                          background: `${item.accentColor}14`,
                          letterSpacing: '0.11em',
                        }}
                      >
                        {item.rank}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>

                <div className="flex items-center justify-between gap-3 mb-4">
                  <span
                    className="font-mono text-[9px] px-2 py-0.5"
                    style={{
                      background: `${item.accentColor}12`,
                      border: `1px solid ${item.accentColor}35`,
                      color: item.accentColor,
                      letterSpacing: '0.14em',
                    }}
                  >
                    {item.id}
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className="font-mono text-[9px] px-2 py-0.5"
                      style={{
                        background: `${item.accentColor}10`,
                        border: `1px solid ${item.accentColor}35`,
                        color: item.accentColor,
                        letterSpacing: '0.12em',
                      }}
                    >
                      {item.rank}
                    </span>
                    <span className="font-mono text-[10px]" style={{ color: 'rgba(200,216,232,0.5)', letterSpacing: '0.12em' }}>
                      {item.year}
                    </span>
                  </div>
                </div>

                <h3 className="font-mono font-black text-xl mb-1" style={{ color: '#FFFFFF' }}>
                  {item.title}
                </h3>
                <p className={`font-mono mb-2 ${isFeatured ? 'text-base' : 'text-sm'}`} style={{ color: item.accentColor }}>
                  {item.event}
                </p>
                <p className="font-mono text-[10px] mb-4" style={{ color: 'rgba(200,216,232,0.45)', letterSpacing: '0.1em' }}>
                  {item.location}
                </p>
                  </>
                )}

                <p className={`font-mono leading-relaxed mb-5 ${isFeatured ? 'text-sm' : 'text-xs'}`} style={{ color: 'rgba(200,216,232,0.66)' }}>
                  {item.summary}
                </p>

                <div className="mb-4 grid grid-cols-[88px_1fr] gap-3 items-start">
                  <button
                    type="button"
                    onClick={() => setPreviewImage({ src: item.certificateImage, title: item.title })}
                    className="relative h-[68px] w-[88px] overflow-hidden"
                    style={{
                      border: `1px solid ${item.accentColor}35`,
                      clipPath: 'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 0 100%)',
                    }}
                    aria-label={`Enlarge certificate for ${item.title}`}
                  >
                    <AppImage
                      src={item.certificateImage}
                      alt={`${item.title} certificate`}
                      fill
                      className="w-full h-full object-cover"
                    />
                  </button>
                  <div>
                    <p className="font-mono text-[9px] mb-2" style={{ color: 'rgba(200,216,232,0.55)', letterSpacing: '0.12em' }}>
                      CERTIFICATE_SNAPSHOT
                    </p>
                    <button
                      type="button"
                      onClick={() => setPreviewImage({ src: item.certificateImage, title: item.title })}
                      className="inline-flex items-center gap-2 font-mono text-[10px] px-3 py-1.5"
                      style={{
                        color: item.accentColor,
                        border: `1px solid ${item.accentColor}40`,
                        background: `${item.accentColor}10`,
                        letterSpacing: '0.12em',
                        clipPath: 'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                      }}
                    >
                      <Icon name="MagnifyingGlassPlusIcon" size={12} />
                      ENLARGE_CERT
                    </button>
                  </div>
                </div>

                <div className="pt-4 space-y-4" style={{ borderTop: `1px solid ${item.accentColor}15` }}>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="font-mono text-[9px] px-2 py-0.5"
                        style={{
                          background: `${item.accentColor}0A`,
                          border: `1px solid ${item.accentColor}25`,
                          color: 'rgba(200,216,232,0.58)',
                          letterSpacing: '0.1em',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <a
                    href={item.postUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 font-mono text-[10px] px-3 py-2 transition-all duration-200 hover:opacity-90"
                    style={{
                      color: item.accentColor,
                      border: `1px solid ${item.accentColor}40`,
                      background: `${item.accentColor}12`,
                      letterSpacing: '0.13em',
                      clipPath: 'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                    aria-label={`Open LinkedIn post for ${item.title}`}
                  >
                    <Icon name="ArrowTopRightOnSquareIcon" size={13} />
                    VIEW_LINKEDIN_POST
                  </a>
                </div>
              </div>
            </article>
          );
        })}
        </div>
      </div>
      {previewImage && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center px-4"
          style={{ background: 'rgba(3,5,7,0.86)' }}
          onClick={() => setPreviewImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Certificate preview"
        >
          <div
            className="relative w-full max-w-4xl p-3 md:p-4 terminal-win"
            style={{
              border: '1px solid rgba(0,212,255,0.35)',
              clipPath: 'polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 0 100%)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-xs text-glow-cyan">{previewImage.title} // CERTIFICATE</p>
              <button
                type="button"
                onClick={() => setPreviewImage(null)}
                className="inline-flex items-center gap-1 font-mono text-[10px] px-2 py-1"
                style={{
                  color: 'var(--cyber-magenta)',
                  border: '1px solid rgba(255,0,110,0.4)',
                  background: 'rgba(255,0,110,0.12)',
                }}
              >
                <Icon name="XMarkIcon" size={12} />
                CLOSE
              </button>
            </div>
            <div className="relative w-full aspect-[16/10] overflow-hidden">
              <AppImage
                src={previewImage.src}
                alt={`${previewImage.title} certificate enlarged`}
                fill
                className="w-full h-full object-contain bg-black"
              />
            </div>
          </div>
        </div>
      )}
      <style jsx>{`
        .achievement-enter {
          animation: achievementIn 550ms ease forwards;
          opacity: 0;
          transform: translateY(18px);
        }
        .scan-line {
          animation: scanMove 2.4s linear infinite;
        }
        @keyframes achievementIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scanMove {
          0% {
            transform: translateX(-40px);
            opacity: 0.15;
          }
          20% {
            opacity: 0.8;
          }
          100% {
            transform: translateX(520px);
            opacity: 0;
          }
        }
      `}</style>
    </section>
  );
}
